AFRAME.registerComponent('aframe-candlestick-chart', {
    schema: {
        intervals: {type: 'number', default: 200},
        multiplier: {type: 'string', default: '5'},
        timespan: {type: 'string', default: 'minute'},
        startDate: {type: 'string', default: ''},
        endDate: {type: 'string', default: ''},
        stockData: {type: 'string', default: ''}, // Full stock data including ticker
        dataInterval: {type: 'string', default: '1d'}, // New schema property for data-interval
        candlestickSpacing: {type: 'number', default: 0.01} // Space between candlesticks in world units
    },

    // Also update the init method to ensure latest data is visible on initialization

    init: async function () {
        // console.log(`init aframe-candlestick-chart with ticker: ${this.stockData ? JSON.parse(this.stockData).ticker : 'N/A'}`);
        // Initialize to show most recent data
        this.visibleStartIndex = 0;

        try {
            this.apiManager = await PolygonAPIManager.create();

            // Parse stockData and extract ticker
            this.stockMeta = this.data.stockData ? JSON.parse(this.data.stockData) : {};
            this.ticker = this.stockMeta.ticker || 'SPY'; // Fallback to 'SPY' if not provided
            // console.log('stockMeta:', this.stockMeta); // Debug: Check stockMeta contents

            // Initialize timezone preference (0 = Local, 1 = Eastern, 2 = UTC)
            this.timezoneMode = 1; // Default to Eastern Time

            // Listen for data-interval events from the scene
            this.dataIntervalListener = (event) => {
                const { interval } = event.detail;
                // console.log(`Stock chart received data-interval: ${interval}`);
                this.el.setAttribute('aframe-candlestick-chart', 'dataInterval', interval.toLowerCase());
            };
            this.el.sceneEl.addEventListener('data-interval', this.dataIntervalListener);

            // Adjust timespan based on data-interval
            this.adjustTimespanFromDataInterval();

            await this.fetchStockData();

            // Create chart elements BEFORE setting initial data visibility
            this.chartPlane = this.createChartPlane();
            this.legendPlane = this.createLegendPlane();
            this.tooltip = this.createTooltip();

            this.timelineLine = this.createTimelineLine();
            this.timezoneButton = this.createTimezoneButton();

            // Set up timeline listener AFTER timeline line is created
            this.setupTimelineListener();

            this.candlesticks = [];
            this.gridLines = [];

            // Add debug code to calculate optimal candlestick count
            this.calculateOptimalCandlestickCount();

            // Set the visible range to show the latest data AFTER chart elements are created
            this.setInitialDataVisible();

            this.el.addEventListener('timeline-data-available', this.handleTimelineData.bind(this));

            this.isDragging = false;
            this.lastX = null;
            this.addEventListeners();
            if (this.stockPoints && this.stockPoints.length) {
                this.renderCandlesticks();
                this.renderGridLines();
                this.updateLegend();

                // Position timeline AFTER candlesticks are rendered with a small delay
                // to allow A-Frame to process the positioning
                setTimeout(() => {
                    this.initializeTimelinePosition();
                }, 100); // 100ms delay should be sufficient
            }
        } catch (error) {
            console.error('Failed to initialize aframe-candlestick-chart:', error);
        }
    },

    handleTimelineData: async function(event) {
        const { timestamps } = event.detail;
        if (!timestamps || timestamps.length === 0) return;

        // Only use timeline dates for non-1d intervals
        const dataInterval = this.data.dataInterval?.toLowerCase() || '1d';
        if (dataInterval === '1d') {
            // console.log('[Chart] Skipping timeline date override for 1d interval - using existing behavior');
            return;
        }

        // console.log(`[Chart] Using timeline data for ${dataInterval} interval:`, timestamps);

        // Extract date range from timeline timestamps
        const startTimestamp = timestamps[0];
        const endTimestamp = timestamps[timestamps.length - 1];

        // Parse dates safely
        let startDate, endDate;

        try {
            if (startTimestamp.includes(' ')) {
                startDate = startTimestamp.split(' ')[0];
            } else if (startTimestamp.includes('T')) {
                startDate = startTimestamp.split('T')[0];
            } else {
                startDate = startTimestamp;
            }

            if (endTimestamp.includes(' ')) {
                endDate = endTimestamp.split(' ')[0];
            } else if (endTimestamp.includes('T')) {
                endDate = endTimestamp.split('T')[0];
            } else {
                endDate = endTimestamp;
            }

            // Validate dates
            const testStart = new Date(startDate);
            const testEnd = new Date(endDate);

            if (isNaN(testStart.getTime()) || isNaN(testEnd.getTime())) {
                console.warn('[Chart] Invalid dates from timeline, skipping');
                return;
            }

            // Add buffer days to ensure we have enough data for timeline animation
            const endDateObj = new Date(endDate);
            endDateObj.setDate(endDateObj.getDate() + 5);
            const bufferedEndDate = endDateObj.toISOString().split('T')[0];

            // Set the exact date range
            this.data.startDate = startDate;
            this.data.endDate = bufferedEndDate;

            // console.log(`[Chart] Set exact date range from timeline for ${dataInterval}: ${startDate} to ${bufferedEndDate}`);

            // Now fetch the stock data with exact range and wait for completion
            // console.log(`[Chart] Starting coordinated data fetch...`);
            await this.fetchStockData();

            // After fetch is complete, update the rendering
            this.setInitialDataVisible();
            this.renderCandlesticks();
            this.renderGridLines();
            this.updateLegend();

            // console.log(`[Chart] Chart rendering complete after timeline data fetch`);

        } catch (error) {
            console.error('[Chart] Error processing timeline data:', error.message);
        }
    },

    initializeTimelinePosition: function() {
        if (!this.timelineLine || !this.stockPoints || this.stockPoints.length === 0) {
            console.warn('Timeline line or stock data not available for initial positioning');
            return;
        }

        const totalDataPoints = this.stockPoints.length;
        const visibleCount = Math.min(this.data.intervals, totalDataPoints);

        let timelineIndex;
        if (this.data.dataInterval.toLowerCase() === '1d') {
            // For 1D charts, show timeline at the most recent data point
            timelineIndex = visibleCount - 1;
        } else {
            // For multi-day charts, show timeline at the first data point
            timelineIndex = 0;
        }

        // Try to get position from actual rendered candlestick first
        let xPosition;
        let positionSource = 'calculated fallback';

        if (this.candlesticks && this.candlesticks.length > timelineIndex * 2) {
            const candlestickElement = this.candlesticks[timelineIndex * 2];
            if (candlestickElement) {
                const candlestickPosition = candlestickElement.getAttribute('position');
                // Check if the position has been properly set by A-Frame
                if (candlestickPosition && typeof candlestickPosition.x !== 'undefined' && candlestickPosition.x !== 0) {
                    xPosition = parseFloat(candlestickPosition.x);
                    positionSource = 'actual candlestick element';
                }
            }
        }

        // Fallback to calculation if we can't get actual position
        if (xPosition === undefined) {
            xPosition = this.calculateCandlestickXPosition(timelineIndex, visibleCount);
        }

        // Set timeline position
        this.timelineLine.setAttribute('position', `${xPosition} 0.2 -0.39`);
        this.timelineLine.setAttribute('visible', true);

        // Force A-Frame to update the position immediately
        this.timelineLine.flushToDOM();

        // console.log(`Timeline initialized at x=${xPosition} (${positionSource}) for ${this.data.dataInterval} chart`);
    },

    calculateOptimalCandlestickCount: function() {
        // console.log('=== CANDLESTICK CHART OPTIMIZATION DEBUG ===');

        // Detect actual chart container dimensions from the chart plane
        let chartWidth = 5.8; // Default fallback
        let startOffset = -2.9; // Default fallback

        if (this.chartPlane) {
            const planeGeometry = this.chartPlane.getAttribute('geometry');
            const planePosition = this.chartPlane.getAttribute('position');

            if (planeGeometry && planeGeometry.width) {
                // Calculate actual chart dimensions from the plane
                const planeWidth = planeGeometry.width;

                // The chart rendering area is typically smaller than the plane
                // Based on renderCandlesticks logic, chart uses ~85% of plane width
                chartWidth = planeWidth * 0.85;
                startOffset = -(chartWidth / 2);

                // console.log(`📐 Detected chart plane dimensions:`, {
                //     planeWidth: planeWidth,
                //     planeHeight: planeGeometry.height || 'not specified',
                //     planePosition: planePosition || 'not specified'
                // });
            }
        } else {
            console.warn('⚠️ Chart plane not found, using default dimensions');
        }

        const spacing = this.data.candlestickSpacing; // Space between candlesticks

        // console.log(`📊 Chart Container:`, {
        //     totalWidth: chartWidth,
        //     startOffset: startOffset,
        //     endOffset: startOffset + chartWidth,
        //     configuredSpacing: spacing,
        //     detectionMethod: this.chartPlane ? 'detected from chart plane' : 'using defaults'
        // });

        // Test different candlestick counts to find optimal fit
        const testCounts = [50, 100, 150, 200, 250, 300, 350, 400];

        // console.log(`🧮 Testing optimal candlestick counts:`);
        // console.log(`Spacing between candlesticks: ${spacing} world units`);

        testCounts.forEach(count => {
            // Calculate spacing requirements (from renderCandlesticks logic)
            const totalSpacingWidth = spacing * (count - 1); // Total space for gaps
            const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
            const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / count);

            // Calculate if this count fits comfortably
            const totalUsedWidth = (candlestickWidth * count) + totalSpacingWidth;
            const remainingWidth = chartWidth - totalUsedWidth;
            const utilizationPercent = (totalUsedWidth / chartWidth * 100).toFixed(1);

            // Determine comfort level
            let comfort = '❌ Too Tight';
            if (remainingWidth > 0.2) comfort = '✅ Very Comfortable';
            else if (remainingWidth > 0.1) comfort = '👍 Comfortable';
            else if (remainingWidth > 0.05) comfort = '⚠️ Acceptable';
            else if (remainingWidth >= 0) comfort = '😬 Tight';

            // console.log(`  ${count} candlesticks:`, {
            //     candlestickWidth: candlestickWidth.toFixed(4),
            //     totalSpacingWidth: totalSpacingWidth.toFixed(3),
            //     totalUsedWidth: totalUsedWidth.toFixed(3),
            //     remainingWidth: remainingWidth.toFixed(3),
            //     utilization: `${utilizationPercent}%`,
            //     comfort: comfort
            // });
        });

        // Find the sweet spot (80-90% utilization)
        let optimalCount = 0;
        let bestUtilization = 0;

        for (let count = 50; count <= 400; count += 10) {
            const totalSpacingWidth = spacing * (count - 1);
            const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
            const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / count);
            const totalUsedWidth = (candlestickWidth * count) + totalSpacingWidth;
            const utilization = (totalUsedWidth / chartWidth) * 100;

            // Look for 80-90% utilization as optimal
            if (utilization >= 80 && utilization <= 90 && utilization > bestUtilization) {
                bestUtilization = utilization;
                optimalCount = count;
            }
        }

        // console.log(`🎯 OPTIMAL RECOMMENDATION:`, {
        //     optimalCandlestickCount: optimalCount,
        //     utilization: `${bestUtilization.toFixed(1)}%`,
        //     currentConfigured: this.data.intervals,
        //     recommendation: optimalCount !== this.data.intervals ?
        //         `Consider changing intervals from ${this.data.intervals} to ${optimalCount}` :
        //         'Current configuration is optimal!'
        // });

        // Compare with current configuration
        const currentTotalSpacing = spacing * (this.data.intervals - 1);
        const currentAvailableWidth = chartWidth - currentTotalSpacing;
        const currentCandlestickWidth = Math.min(0.06, currentAvailableWidth / this.data.intervals);
        const currentTotalUsed = (currentCandlestickWidth * this.data.intervals) + currentTotalSpacing;
        const currentUtilization = (currentTotalUsed / chartWidth) * 100;

        // console.log(`📋 CURRENT CONFIGURATION ANALYSIS:`, {
        //     configuredCount: this.data.intervals,
        //     actualCandlestickWidth: currentCandlestickWidth.toFixed(4),
        //     totalSpacingUsed: currentTotalSpacing.toFixed(3),
        //     totalWidthUsed: currentTotalUsed.toFixed(3),
        //     utilization: `${currentUtilization.toFixed(1)}%`,
        //     efficiency: currentUtilization >= 80 && currentUtilization <= 90 ? '✅ Efficient' :
        //         currentUtilization < 80 ? '⚠️ Under-utilized' : '❌ Over-packed'
        // });

        // console.log('=== END OPTIMIZATION DEBUG ===');
    },

    update: async function (oldData) {
        if (Object.keys(oldData).length && this.apiManager) {
            const changedKeys = Object.keys(this.data).filter(key => this.data[key] !== oldData[key]);
            if (changedKeys.length) {
                // Check if dataInterval changed and adjust timespan accordingly
                if (changedKeys.includes('dataInterval')) {
                    this.adjustTimespanFromDataInterval();
                }

                this.stockMeta = this.data.stockData ? JSON.parse(this.data.stockData) : {};
                this.ticker = this.stockMeta.ticker || 'SPY';
                await this.fetchStockData().then(() => {
                    // Set the visible range to show the latest data after fetch
                    this.setInitialDataVisible();
                    this.renderCandlesticks();
                    this.renderGridLines();
                    this.updateLegend();
                });
            }
        }
    },

    remove: function () {
        if (this.refreshInterval) clearInterval(this.refreshInterval);
        this.clearGridLines();

        // Remove timeline event listeners
        if (this.gotoTimeListener) {
            this.el.sceneEl.removeEventListener('goto-time', this.gotoTimeListener);
        }
        if (this.timelineUpdateListener) {
            this.el.sceneEl.removeEventListener('timeline-update', this.timelineUpdateListener);
        }

        // Remove data-interval event listener
        if (this.dataIntervalListener) {
            this.el.sceneEl.removeEventListener('data-interval', this.dataIntervalListener);
        }

        // Remove timezone button click listener
        if (this.timezoneButtonListener) {
            this.timezoneButton?.removeEventListener('click', this.timezoneButtonListener);
        }
    },

    setupTimelineListener: function() {
        if (!this.el.sceneEl) return;

        // Listen for goto-time events from timeline-control (manual interactions)
        this.gotoTimeListener = (event) => {
            const { timestamp, index } = event.detail;
            // console.log(`[Chart] Timeline goto-time: timestamp="${timestamp}", timeline-index=${index}`);

            if (!this.stockPoints || !this.stockPoints.length) {
                console.warn('[Chart] No stock data available for timeline update');
                return;
            }

            const candlestickIndex = this.findCandlestickIndexByTimestamp(timestamp);

            if (candlestickIndex >= 0) {
                // console.log(`[Chart] Found candlestick at index=${candlestickIndex} for timestamp="${timestamp}"`);
                this.handleTimelineUpdate(timestamp, candlestickIndex);
            } else {
                console.warn(`[Chart] Could not find candlestick for timestamp="${timestamp}"`);
            }
        };

        // Listen for timeline-update events from stock-cube-visualizer (animation updates)
        this.timelineUpdateListener = (event) => {
            const { timestamp, index, total } = event.detail;
            // console.log(`[Chart] Stock cube timeline-update: timestamp="${timestamp}", index=${index}, total=${total}`);

            if (!this.stockPoints || !this.stockPoints.length) {
                console.warn('[Chart] No stock data available for cube timeline update');
                return;
            }

            const candlestickIndex = this.findCandlestickIndexByTimestamp(timestamp);

            if (candlestickIndex >= 0) {
                // console.log(`[Chart] Found candlestick at index=${candlestickIndex} for cube timeline timestamp="${timestamp}"`);
                this.handleTimelineUpdate(timestamp, candlestickIndex);
            } else {
                console.warn(`[Chart] Could not find candlestick for cube timeline timestamp="${timestamp}"`);
            }
        };

        // Add both event listeners
        this.el.sceneEl.addEventListener('goto-time', this.gotoTimeListener);
        this.el.sceneEl.addEventListener('timeline-update', this.timelineUpdateListener);

        // console.log('[Chart] Both timeline listeners set up - goto-time and timeline-update');
    },


        // New helper method to find the End of Day (4 PM ET) candlestick for a given date
        // New helper method to find the End of Day (4 PM ET) candlestick for a given date
    
    findCandlestickIndexByTimestamp: function(targetTimestamp) {
        if (!this.stockPoints || this.stockPoints.length === 0) {
            console.warn('[Chart] No stock data available for timestamp search');
            return -1;
        }
    
        // Parse the target timestamp 
        let targetDate;
        
        // Handle EDT/EST timezone conversion
        if (targetTimestamp.includes('EDT') || targetTimestamp.includes('EST')) {
            // Remove timezone suffix and parse
            const cleanTimestamp = targetTimestamp.replace(/\s*(EDT|EST)$/, '');
            const parsedDate = new Date(cleanTimestamp);
            
            // Convert EDT/EST to UTC for comparison with stock data
            // EDT is UTC-4 (subtract 4 hours from EDT to get UTC)
            // EST is UTC-5 (subtract 5 hours from EST to get UTC)
            const isEDT = targetTimestamp.includes('EDT');
            const timezoneOffset = isEDT ? 3 : 4; // hours to subtract to convert to UTC
            targetDate = new Date(parsedDate.getTime() - (timezoneOffset * 60 * 60 * 1000));

            // console.log(`[Chart] Converted timeline timestamp: ${targetTimestamp} -> ${cleanTimestamp} -> UTC: ${targetDate.toISOString()}`);
            // console.log(`[Chart] Timezone conversion: ${isEDT ? 'EDT (UTC-4)' : 'EST (UTC-5)'} - subtracted ${timezoneOffset} hours`);
        } else {
            targetDate = new Date(targetTimestamp);
        }

        if (isNaN(targetDate.getTime())) {
            console.warn('[Chart] Invalid timestamp for candlestick search:', targetTimestamp);
            return -1;
        }

        let bestMatchIndex = -1;
        let minTimeDiff = Infinity;
        const maxAcceptableTimeDiff = 30 * 60 * 1000; // 30 minutes tolerance

         console.log(`[Chart] Searching for candlestick matching: ${targetDate.toISOString()}`);
         console.log(`[Chart] Available data range: ${this.stockPoints.length} points from ${new Date(this.stockPoints[0].DateTime || this.stockPoints[0].t).toISOString()} to ${new Date(this.stockPoints[this.stockPoints.length-1].DateTime || this.stockPoints[this.stockPoints.length-1].t).toISOString()}`);

        // Search through all stock points to find the best match
            // For 5D charts, we need to look for candlesticks from the target date
        this.stockPoints.forEach((point, index) => {
            // Handle both data formats
            const pointTimestamp = point.DateTime || point.t;

            if (!pointTimestamp) return;

            const pointDate = new Date(pointTimestamp);
            const timeDiff = Math.abs(pointDate.getTime() - targetDate.getTime());

            if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                bestMatchIndex = index;
            }

            // Debug logging for first few and around target time
            const timeDiffMinutes = Math.round(timeDiff / 60000);
            if (index < 5 || timeDiffMinutes <= 30) {
                console.log(`[Chart] Point[${index}]: ${pointTimestamp} -> UTC: ${pointDate.toISOString()}, diff: ${timeDiffMinutes}min`);
            }
        });

        if (bestMatchIndex >= 0 && minTimeDiff <= maxAcceptableTimeDiff) {
            const matchedPoint = this.stockPoints[bestMatchIndex];
            const matchedTimestamp = matchedPoint.DateTime || matchedPoint.t;
            const timeDiffMinutes = Math.round(minTimeDiff / 60000);

            console.log(`[Chart] Best match found: index=${bestMatchIndex}, timestamp=${matchedTimestamp}, diff=${timeDiffMinutes}min`);
            return bestMatchIndex;
        } else {
            const timeDiffMinutes = Math.round(minTimeDiff / 60000);
            console.warn(`[Chart] No suitable match found. Best was index=${bestMatchIndex} with ${timeDiffMinutes}min difference (max allowed: ${maxAcceptableTimeDiff/60000}min)`);
            return -1;
        }
    },

    setInitialDataVisible: function() {
        if (!this.stockPoints || this.stockPoints.length === 0) {
            console.warn('No stock data available for initial visibility setup');
            return;
        }

        const totalDataPoints = this.stockPoints.length;
        const visibleCount = 100; // Always show 100 candlesticks

        // Always start with the earliest data (left-aligned)
        this.visibleStartIndex = 0;
        this.visibleEndIndex = Math.min(visibleCount - 1, totalDataPoints - 1);

        // console.log(`📊 Initial data visibility: showing candlesticks 0-${this.visibleEndIndex} out of ${totalDataPoints} total (100 per page)`);
        // console.log(`📊 Data interval: ${this.data.dataInterval}, showing earliest data first`);
    },

    handleTimelineUpdate: function(timestamp, candlestickIndex) {
        // console.log(`[Chart] Handling timeline update: ${timestamp} -> candlestick index ${candlestickIndex}`);

        if (candlestickIndex < 0 || candlestickIndex >= this.stockPoints.length) {
            console.warn(`[Chart] Invalid candlestick index: ${candlestickIndex} (valid range: 0-${this.stockPoints.length-1})`);
            return;
        }

        // Update the timeline line position
        this.updateTimelineLinePosition(candlestickIndex);

        // Update any additional chart elements
        this.updateChartForTimestamp(candlestickIndex);
    },

    updateTimelineLinePosition: function(candlestickIndex) {
        if (!this.timelineLine || !this.stockPoints || !this.stockPoints.length) {
            return;
        }

        const totalDataPoints = this.stockPoints.length;
        const visibleCount = 100; // Always 100 candlesticks per page

        // Get current visible range
        let currentVisibleStartIndex = this.visibleStartIndex || 0;
        let currentVisibleEndIndex = currentVisibleStartIndex + visibleCount - 1;

        // console.log(`[Chart] Timeline positioning: candlestick=${candlestickIndex}, currentVisible=[${currentVisibleStartIndex}-${currentVisibleEndIndex}], totalData=${totalDataPoints}`);

        // Check if the candlestick is within the current visible range
        if (candlestickIndex >= currentVisibleStartIndex && candlestickIndex <= currentVisibleEndIndex) {
            // Candlestick is visible - position timeline within current view
            const relativeIndex = candlestickIndex - currentVisibleStartIndex;

            // console.log(`[Chart] Candlestick is visible: global=${candlestickIndex}, visibleStart=${currentVisibleStartIndex}, relative=${relativeIndex}`);

            // Get the actual X position
            let xPosition;
            if (this.candlesticks && this.candlesticks.length > relativeIndex * 2) {
                const candlestickElement = this.candlesticks[relativeIndex * 2];
                if (candlestickElement) {
                    const candlestickPosition = candlestickElement.getAttribute('position');
                    xPosition = parseFloat(candlestickPosition.x);
                }
            }

            // Fallback to calculation if we can't get the actual position
            if (xPosition === undefined) {
                xPosition = this.calculateCandlestickXPosition(relativeIndex, visibleCount);
            }

            // Animate timeline to new position
            this.timelineLine.removeAttribute('animation__position');
            this.timelineLine.setAttribute('animation__position', {
                property: 'position',
                from: this.timelineLine.getAttribute('position'),
                to: `${xPosition} 0.2 -0.39`,
                dur: 400,
                easing: 'easeInOutQuad'
            });

            this.timelineLine.setAttribute('visible', true);
            // console.log(`[Chart] Timeline animated to x=${xPosition} for candlestick ${candlestickIndex}`);

        } else {
            // Candlestick is outside visible range - check if we need to scroll to next page
            // console.log(`[Chart] Candlestick ${candlestickIndex} outside visible range, checking for page scroll...`);

            // Determine if we should scroll forward (timeline reached end of current page)
            if (candlestickIndex > currentVisibleEndIndex) {
                // Calculate new page start - try to center the target candlestick in new page
                let newVisibleStartIndex = Math.max(0, candlestickIndex - 50); // Center in 100-candlestick page

                // Ensure we don't exceed data bounds
                const maxPossibleStartIndex = Math.max(0, totalDataPoints - visibleCount);
                newVisibleStartIndex = Math.min(newVisibleStartIndex, maxPossibleStartIndex);

                // Update the visible range
                this.visibleStartIndex = newVisibleStartIndex;
                this.visibleEndIndex = Math.min(newVisibleStartIndex + visibleCount - 1, totalDataPoints - 1);

                // console.log(`[Chart] 📄 Scrolled to next page: showing candlesticks ${this.visibleStartIndex}-${this.visibleEndIndex}`);

                // Re-render the chart with new visible range
                this.renderCandlesticks();
                this.renderGridLines();
                this.updateLegend();

                // Position timeline at the target candlestick in the new page
                setTimeout(() => {
                    const newRelativeIndex = candlestickIndex - this.visibleStartIndex;

                    let xPosition;
                    if (this.candlesticks && this.candlesticks.length > newRelativeIndex * 2) {
                        const candlestickElement = this.candlesticks[newRelativeIndex * 2];
                        if (candlestickElement) {
                            const candlestickPosition = candlestickElement.getAttribute('position');
                            xPosition = parseFloat(candlestickPosition.x);
                        }
                    }

                    if (xPosition === undefined) {
                        xPosition = this.calculateCandlestickXPosition(newRelativeIndex, visibleCount);
                    }

                    this.timelineLine.setAttribute('position', `${xPosition} 0.2 -0.39`);
                    this.timelineLine.setAttribute('visible', true);

                    // console.log(`[Chart] Timeline positioned at x=${xPosition} on new page`);
                }, 50);

            } else if (candlestickIndex < currentVisibleStartIndex) {
                // Scroll backward if needed
                let newVisibleStartIndex = Math.max(0, candlestickIndex - 50);

                this.visibleStartIndex = newVisibleStartIndex;
                this.visibleEndIndex = Math.min(newVisibleStartIndex + visibleCount - 1, totalDataPoints - 1);

                // console.log(`[Chart] 📄 Scrolled to previous page: showing candlesticks ${this.visibleStartIndex}-${this.visibleEndIndex}`);

                this.renderCandlesticks();
                this.renderGridLines();
                this.updateLegend();

                setTimeout(() => {
                    const newRelativeIndex = candlestickIndex - this.visibleStartIndex;
                    let xPosition = this.calculateCandlestickXPosition(newRelativeIndex, visibleCount);

                    this.timelineLine.setAttribute('position', `${xPosition} 0.2 -0.39`);
                    this.timelineLine.setAttribute('visible', true);

                    // console.log(`[Chart] Timeline positioned at x=${xPosition} on previous page`);
                }, 50);
            }
        }
    },

    updateChartForTimestamp: function(candlestickIndex) {
        // Update any chart-specific elements that depend on the current timestamp
            // This could include highlighting the current candlestick, updating tooltips, etc.

        if (this.stockPoints && this.stockPoints[candlestickIndex]) {
            const currentPoint = this.stockPoints[candlestickIndex];
            // console.log(`[Chart] Current data point:`, currentPoint);

            // Handle different data structures
                // Handle different data structures - some use 't', 'c', 'v' and others use 'DateTime', 'Close', 'Volume'
            let timestamp, close, volume;

            if (currentPoint.t !== undefined) {
                // Polygon API format
                timestamp = currentPoint.t;
                close = currentPoint.c;
                volume = currentPoint.v;
            } else if (currentPoint.DateTime !== undefined) {
                // Alternative API format
                timestamp = currentPoint.DateTime;
                close = currentPoint.Close;
                volume = currentPoint.Volume;
            } else {
                console.warn('[Chart] Unknown data point structure:', currentPoint);
                return;
            }

            // Validate timestamp
            const dateObj = new Date(timestamp);
            if (isNaN(dateObj.getTime())) {
                console.warn(`[Chart] Invalid timestamp: ${timestamp}`);
                return;
            }

            // console.log(`[Chart] Current data point details:`, {
            //     candlestickIndex: candlestickIndex,
            //     timestamp: dateObj.toISOString(),
            //     close: close,
            //     volume: volume
            // });

                // You can add more chart-specific updates here
            // Highlight the current candlestick
            this.highlightCurrentCandlestick(candlestickIndex);
        }
    },

    highlightCurrentCandlestick: function(candlestickIndex) {
        // Reset previous highlights
        if (this.highlightedCandlestick) {
            this.highlightedCandlestick.setAttribute('material', 'opacity', 1.0);
            this.highlightedCandlestick = null;
        }

        // Highlight current candlestick if visible
        const visibleStartIndex = this.visibleStartIndex || Math.max(0, this.stockPoints.length - this.data.intervals);
        const relativeIndex = candlestickIndex - visibleStartIndex;

        // console.log(`[Chart] Highlighting: candlestick=${candlestickIndex}, visibleStart=${visibleStartIndex}, relative=${relativeIndex}`);

        if (relativeIndex >= 0 && relativeIndex < this.data.intervals && this.candlesticks && this.candlesticks[relativeIndex]) {
            const candlestick = this.candlesticks[relativeIndex];
            candlestick.setAttribute('material', 'opacity', 0.7);
            this.highlightedCandlestick = candlestick;
            // console.log(`[Chart] Highlighted candlestick at relative index ${relativeIndex}`);
        } else {
            // console.log(`[Chart] Cannot highlight - relative index ${relativeIndex} out of range [0-${this.data.intervals-1}] or candlestick not found`);
        }
    },

    findCandlestickByTimestamp: function(targetTimestamp) {
        if (!this.stockPoints || this.stockPoints.length === 0) return -1;

        // Parse the target timestamp and handle EDT/EST timezone properly
        let targetDate;

        // Check if the timestamp includes timezone info (EDT/EST)
        if (targetTimestamp.includes('EDT') || targetTimestamp.includes('EST')) {
            // Remove timezone suffix and parse as EDT/EST
            const cleanTimestamp = targetTimestamp.replace(/\s*(EDT|EST)$/, '');

            // Parse the clean timestamp
            const parsedDate = new Date(cleanTimestamp);

            // Convert EDT/EST to UTC for comparison with stock data
            // EDT is UTC-4, EST is UTC-5
            const isEDT = targetTimestamp.includes('EDT');
            const timezoneOffset = isEDT ? 4 : 5; // hours to add to convert to UTC

            targetDate = new Date(parsedDate.getTime() + (timezoneOffset * 60 * 60 * 1000));

            // console.log(`Converted ${targetTimestamp} -> ${cleanTimestamp} -> ${targetDate.toISOString()}`);
        } else {
            targetDate = new Date(targetTimestamp);
        }

        if (isNaN(targetDate.getTime())) {
            console.warn('Invalid timestamp:', targetTimestamp);
            return -1;
        }

        const isIndexTicker = this.ticker && this.ticker.startsWith('I:');

        // console.log(`Looking for candlestick matching timestamp: ${targetTimestamp} (converted to UTC: ${targetDate.toISOString()})`);
        // console.log(`Index ticker: ${isIndexTicker}, Current timezone mode: ${this.useLocalTimezone ? 'Local' : 'ET'}`);

        let bestMatch = -1;
        let minTimeDiff = Infinity;
        const maxAcceptableTimeDiff = 30 * 60 * 1000; // 30 minutes in milliseconds

        // Search through visible candlesticks first
        const searchRange = this.stockPoints.slice(this.visibleStartIndex, this.visibleStartIndex + this.data.intervals);

        searchRange.forEach((point, relativeIndex) => {
            const candlestickDate = new Date(point.DateTime);
            const timeDiff = Math.abs(candlestickDate.getTime() - targetDate.getTime());

            if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                bestMatch = this.visibleStartIndex + relativeIndex;
            }

            // Debug: Show comparison for first few points
            if (relativeIndex < 5) {
                // console.log(`  Candlestick[${this.visibleStartIndex + relativeIndex}]: ${point.DateTime} (${candlestickDate.toISOString()}) - diff: ${timeDiff}ms (${Math.round(timeDiff/60000)}min)`);
            }
        });

        // If no close match in visible range (more than 30 minutes off), search all data
        if (minTimeDiff > maxAcceptableTimeDiff) {
            // console.log(`No close match in visible range (best was ${Math.round(minTimeDiff/60000)}min off), searching all data...`);

            minTimeDiff = Infinity;
            bestMatch = -1;

            this.stockPoints.forEach((point, index) => {
                const candlestickDate = new Date(point.DateTime);
                const timeDiff = Math.abs(candlestickDate.getTime() - targetDate.getTime());

                if (timeDiff < minTimeDiff) {
                    minTimeDiff = timeDiff;
                    bestMatch = index;
                }
            });

            // If we found a better match in all data, check if we need to adjust the visible range
            if (bestMatch !== -1 && minTimeDiff <= maxAcceptableTimeDiff) {
                const targetVisibleIndex = bestMatch - this.visibleStartIndex;

                // If the target is outside the visible range, adjust the visible range
                if (targetVisibleIndex < 0 || targetVisibleIndex >= this.data.intervals) {
                    // console.log(`Target candlestick at index ${bestMatch} is outside visible range. Adjusting view...`);

                    // Center the target in the visible range
                    const newVisibleStartIndex = Math.max(0, bestMatch - Math.floor(this.data.intervals / 2));
                    const maxStartIndex = Math.max(0, this.stockPoints.length - this.data.intervals);

                    this.visibleStartIndex = Math.min(newVisibleStartIndex, maxStartIndex);

                    // Re-render the chart with new visible range
                    this.renderCandlesticks();
                    this.renderGridLines();
                    this.updateLegend();

                    // console.log(`Adjusted visible range: start=${this.visibleStartIndex}, showing candlesticks ${this.visibleStartIndex} to ${this.visibleStartIndex + this.data.intervals - 1}`);
                }
            }
        }

        if (bestMatch !== -1) {
            const matchedPoint = this.stockPoints[bestMatch];
            const matchedDate = new Date(matchedPoint.DateTime);
            const timeDiffMinutes = Math.round(minTimeDiff / 60000);

            // console.log(`Best match: index ${bestMatch}, timestamp ${matchedPoint.DateTime} (${matchedDate.toISOString()}), diff: ${minTimeDiff}ms (${timeDiffMinutes}min)`);

            // Convert global index to visible index for positioning
            const visibleIndex = bestMatch - this.visibleStartIndex;
            if (visibleIndex >= 0 && visibleIndex < this.data.intervals) {
                return visibleIndex; // Return relative position within visible range
            } else {
                // console.log(`Matched candlestick is outside visible range. Global index: ${bestMatch}, Visible range: ${this.visibleStartIndex}-${this.visibleStartIndex + this.data.intervals - 1}`);
                return -1;
            }
        }

        console.warn(`No suitable candlestick found for timestamp: ${targetTimestamp}`);
        return -1;
    },

    calculateCandlestickXPosition: function(relativeIndex, visibleCount) {
        if (!this.chartPlane) {
            console.warn('Chart plane not available for position calculation, using fallback');
            // Use fallback positioning logic from renderCandlesticks
            const chartWidth = 5.8;
            const startOffset = -2.9;
            const spacing = this.data.candlestickSpacing;
            const totalSpacingWidth = spacing * (visibleCount - 1);
            const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
            const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / visibleCount);
            
            // Calculate position with spacing (same logic as renderCandlesticks)
            const spacingOffset = relativeIndex * spacing;
            const candlestickOffset = relativeIndex * candlestickWidth;
            return startOffset + spacingOffset + candlestickOffset + candlestickWidth/2;
        }

        // Get chart plane geometry
        const planeGeometry = this.chartPlane.getAttribute('geometry');
        if (!planeGeometry || !planeGeometry.width) {
            console.warn('Chart plane geometry not available, using fallback');
            // Use same fallback as above
            const chartWidth = 5.8;
            const startOffset = -2.9;
            const spacing = this.data.candlestickSpacing;
            const totalSpacingWidth = spacing * (visibleCount - 1);
            const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
            const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / visibleCount);
            
            const spacingOffset = relativeIndex * spacing;
            const candlestickOffset = relativeIndex * candlestickWidth;
            return startOffset + spacingOffset + candlestickOffset + candlestickWidth/2;
        }

        // Use actual chart plane dimensions
        const planeWidth = planeGeometry.width;
        const chartWidth = planeWidth * 0.85; // Chart uses 85% of plane width
        const startOffset = -(chartWidth / 2);
        
        // Calculate candlestick positioning (matching renderCandlesticks logic)
        const spacing = this.data.candlestickSpacing;
        const totalSpacingWidth = spacing * (visibleCount - 1);
        const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
        const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / visibleCount);
        
        // Calculate position with spacing (same logic as renderCandlesticks)
        const spacingOffset = relativeIndex * spacing;
        const candlestickOffset = relativeIndex * candlestickWidth;
        const xPosition = startOffset + spacingOffset + candlestickOffset + candlestickWidth/2;
        
        // console.log(`Timeline position calc: relativeIndex=${relativeIndex}, xPosition=${xPosition}, chartWidth=${chartWidth}`);
        
        return xPosition;
    },


    adjustTimespanFromDataInterval: function () {
        const dataInterval = this.data.dataInterval.toLowerCase();

        // Standardize to 100 candlesticks per page for all intervals
        let targetCandlesticks = 100;
        let multiplier, timespan;

        switch (dataInterval) {
            case '1d':
                multiplier = '5';
                timespan = 'minute';
                break;
            case '5d':
                multiplier = '30';
                timespan = 'minute';
                break;
            case '1m':
                multiplier = '4';
                timespan = 'hour';
                break;
            default:
                console.warn(`Unknown data-interval: ${dataInterval}, keeping current timespan settings`);
                return;
        }

        // Update the schema properties - always use 100 candlesticks
        this.data.intervals = targetCandlesticks;
        this.data.multiplier = multiplier;
        this.data.timespan = timespan;

        // Always set dates if not already set
        if (!this.data.startDate || !this.data.endDate) {
            let endDate = new Date();
            const isIndexTicker = this.ticker && this.ticker.startsWith('I:');

            if (isIndexTicker && dataInterval === '1d') {
                try {
                    const edtDate = new Date(endDate.toLocaleString("en-US", {timeZone: "America/New_York"}));
                    endDate = new Date(edtDate.getFullYear(), edtDate.getMonth(), edtDate.getDate());
                    // console.log(`🔧 Index ticker ${this.ticker} with 1D interval: capping endDate to today in EDT`);
                } catch (error) {
                    console.warn('Error setting EDT date, using current date:', error.message);
                    endDate = new Date();
                }
            }

            if (isNaN(endDate.getTime())) {
                console.error('Invalid endDate created, using current date');
                endDate = new Date();
            }

            this.data.endDate = endDate.toISOString().split('T')[0];

            // Calculate start date with reasonable defaults based on interval
            const daysBack = dataInterval === '1d' ? 1 : (dataInterval === '5d' ? 15 : 45);
            const startDate = new Date(endDate.getTime() - (daysBack * 24 * 60 * 60 * 1000));

            if (isNaN(startDate.getTime())) {
                console.error('Invalid startDate created, using fallback');
                const fallbackStart = new Date();
                fallbackStart.setDate(fallbackStart.getDate() - daysBack);
                this.data.startDate = fallbackStart.toISOString().split('T')[0];
            } else {
                this.data.startDate = startDate.toISOString().split('T')[0];
            }

            // console.log(`📊 Set date range for ${dataInterval}: ${this.data.startDate} to ${this.data.endDate}`);
        }

        // console.log(`📊 Optimized parameters for ${dataInterval}:`, {
        //     ticker: this.ticker,
        //     targetCandlesticks,
        //     multiplier,
        //     timespan,
        //     startDate: this.data.startDate,
        //     endDate: this.data.endDate
        // });
    },

    calculateExpectedTimeRange: function(candlesticks, multiplier, timespan) {
        const mult = parseInt(multiplier);
        let totalMinutes;

        switch(timespan) {
            case 'minute':
                totalMinutes = candlesticks * mult;
                break;
            case 'hour':
                totalMinutes = candlesticks * mult * 60;
                break;
            case 'day':
                totalMinutes = candlesticks * mult * 60 * 24;
                break;
            default:
                totalMinutes = candlesticks * mult;
        }

        const tradingHours = totalMinutes / 60;
        const tradingDays = tradingHours / 6.5; // Assuming 6.5 hour trading day

        return {
            totalMinutes,
            tradingHours: Math.round(tradingHours * 10) / 10,
            tradingDays: Math.round(tradingDays * 10) / 10,
            description: `${candlesticks} × ${mult} ${timespan}${mult > 1 ? 's' : ''} = ~${Math.round(tradingDays)} trading days`
        };
    },


    fetchStockData: async function () {
        // Emit fetch started event
        this.el.sceneEl.emit('chart-data-fetch-started', {
            ticker: this.ticker,
            dataInterval: this.data.dataInterval
        });

        try {
            const { stockPoints, splits } = await this.apiManager.fetchStockAndSplits(
                this.ticker,
                this.data.multiplier,
                this.data.timespan,
                this.data.startDate,
                this.data.endDate
            );

            this.stockPoints = stockPoints.sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
            this.splits = splits;

            // Set the visible range to show the latest data after data is loaded
            this.setInitialDataVisible();

            console.info(`Fetched ${stockPoints.length} stock points and ${splits.length} splits for ${this.ticker}`);
            console.info(`Fetched ${this.ticker} stock points.`, this.stockPoints);

            // Calculate date range for the event
            let dateRange = null;
            if (this.stockPoints.length > 0) {
                const firstPoint = this.stockPoints[0];
                const lastPoint = this.stockPoints[this.stockPoints.length - 1];
                dateRange = {
                    start: firstPoint.DateTime,
                    end: lastPoint.DateTime
                };
            }

            // Emit fetch complete event
            this.el.sceneEl.emit('chart-data-fetch-complete', {
                ticker: this.ticker,
                dataInterval: this.data.dataInterval,
                pointCount: this.stockPoints.length,
                dateRange: dateRange
            });

        } catch (error) {
            console.error('Error fetching stock data:', error);
            this.stockPoints = [];
            this.splits = [];

            // Emit fetch error event
            this.el.sceneEl.emit('chart-data-fetch-error', {
                ticker: this.ticker,
                error: error.message
            });

            throw error; // Re-throw to allow calling code to handle if needed
        }
    },

    createTimezoneButton: function () {
        // Create ui-button for timezone toggle - positioned in bottom right corner of chart
        const timezoneButton = document.createElement('ui-button');
        timezoneButton.setAttribute('label', 'ET TZ');
        timezoneButton.setAttribute('height', '.35');
        timezoneButton.setAttribute('color', 'green');
        timezoneButton.setAttribute('type', 'rounded-box');
        timezoneButton.setAttribute('autosize', false);

        timezoneButton.setAttribute('position', '2.5 -2.2 0'); // Bottom right corner of chart
        timezoneButton.setAttribute('data-raycastable', '');

        this.el.appendChild(timezoneButton);

        // Store reference to the button for updating label
        this.timezoneButton = timezoneButton;

        // Add click listener
        this.timezoneButtonListener = () => {
            this.toggleTimezone();
        };
        timezoneButton.addEventListener('click', this.timezoneButtonListener);

        return timezoneButton;
    },

    toggleTimezone: function () {
        // Cycle through three timezone modes: 0 = Local, 1 = Eastern, 2 = UTC
        this.timezoneMode = (this.timezoneMode + 1) % 3;

        // Update button label based on current mode
        let buttonText;
        switch (this.timezoneMode) {
            case 0:
                buttonText = 'Local TZ';
                break;
            case 1:
                buttonText = 'ET TZ';
                break;
            case 2:
                buttonText = 'UTC TZ';
                break;
            default:
                buttonText = 'ET TZ';
                this.timezoneMode = 1;
        }

        // Update button label using ui-button's label attribute
        this.timezoneButton.setAttribute('label', buttonText);

        // Re-render grid lines and tooltips to show updated times
        this.renderGridLines();

        // console.log(`Timezone switched to: ${buttonText} (mode: ${this.timezoneMode})`);
    },

    formatTimeForTimezone: function (date) {
        switch (this.timezoneMode) {
            case 0: // Local timezone
                return {
                    date: date.toLocaleDateString(),
                    time: date.toLocaleTimeString('en-US', {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }),
                    timezone: 'Local'
                };

            case 1: // Eastern Time
                return {
                    date: date.toLocaleDateString('en-US', {
                        timeZone: 'America/New_York'
                    }),
                    time: date.toLocaleTimeString('en-US', {
                        timeZone: 'America/New_York',
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }),
                    timezone: 'ET'
                };

            case 2: // UTC
                return {
                    date: date.toLocaleDateString('en-US', {
                        timeZone: 'UTC'
                    }),
                    time: date.toLocaleTimeString('en-US', {
                        timeZone: 'UTC',
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }),
                    timezone: 'UTC'
                };

            default: // Fallback to Eastern Time
                return {
                    date: date.toLocaleDateString('en-US', {
                        timeZone: 'America/New_York'
                    }),
                    time: date.toLocaleTimeString('en-US', {
                        timeZone: 'America/New_York',
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }),
                    timezone: 'ET'
                };
        }
    },

    addEventListeners: function () {
        this.el.addEventListener('mousedown', (event) => {
            this.isDragging = true;
            this.lastX = event.clientX;
        });
        document.addEventListener('mousemove', (event) => {
            if (!this.isDragging || !this.lastX) return;
            const deltaX = event.clientX - this.lastX;
            this.handleScroll(deltaX);
            this.lastX = event.clientX;
        });
        document.addEventListener('mouseup', () => {
            this.isDragging = false;
            this.lastX = null;
        });
        this.el.addEventListener('touchstart', (event) => {
            this.isDragging = true;
            this.lastX = event.touches[0].clientX;
        });
        this.el.addEventListener('touchmove', (event) => {
            if (!this.isDragging || !this.lastX) return;
            const deltaX = event.touches[0].clientX - this.lastX;
            this.handleScroll(deltaX);
            this.lastX = event.touches[0].clientX;
        });
        this.el.addEventListener('touchend', () => {
            this.isDragging = false;
            this.lastX = null;
        });
        this.el.addEventListener('axismove', (event) => {
            const axisValue = event.detail.axis[0];
            this.handleScroll(-axisValue * 10);
        });

    },

    handleScroll: function (deltaX) {
        if (!this.stockPoints || this.stockPoints.length <= this.data.intervals) return;
        const scrollSensitivity = 0.1;
        const indexChange = Math.round(deltaX * scrollSensitivity);
        this.visibleStartIndex = Math.max(0, Math.min(this.stockPoints.length - this.data.intervals, this.visibleStartIndex - indexChange));
        this.renderCandlesticks();
        this.renderGridLines();
        this.updateLegend();
    },

    createChartPlane: function () {
        let plane = document.createElement('a-plane');
        plane.setAttribute('width', '6.8');
        plane.setAttribute('height', '4');
        plane.setAttribute('opacity', '0.01');
        plane.setAttribute('color', '#FFF');
        plane.setAttribute('position', '0 0.2 -0.4');
        plane.setAttribute('rotation', '0 0 0');
        this.el.appendChild(plane);
        return plane;
    },

    createLegendPlane: function () {
        let legendPlane = document.createElement('a-entity');
        legendPlane.setAttribute('width', '2.2');
        legendPlane.setAttribute('height', '0.8');
        legendPlane.setAttribute('color', '');
        let legendBackground = document.createElement('a-rounded-plane');
        legendBackground.setAttribute('position', "-1.1 -0.5 .01");
        legendBackground.setAttribute('width', "2.2");
        legendBackground.setAttribute('height', "0.8");
        legendBackground.setAttribute('color', '#333');
        legendBackground.setAttribute('border-color', 'white');

        this.legendText = document.createElement('a-troika-text');
        this.legendText.setAttribute('position', '-1 .2 0.05');
        this.legendText.setAttribute('value', '');
        this.legendText.setAttribute("color", "white");
        this.legendText.setAttribute("line-height", "1");
        this.legendText.setAttribute("font-size", "0.1");
        this.legendText.setAttribute("align", "left");
        this.legendText.setAttribute("anchor", "left");
        this.legendText.setAttribute("baseline", "top");
        this.legendText.setAttribute("max-width", "3");
        this.legendText.setAttribute("depth-offset", "1");
        this.legendText.setAttribute("overflow-wrap", "normal");
        legendPlane.appendChild(this.legendText);

        legendPlane.appendChild(legendBackground);
        this.el.appendChild(legendPlane);
        return legendPlane;
    },

    createTooltip: function () {
        let tooltip = document.createElement('a-entity');
        tooltip.setAttribute('geometry', {primitive: 'plane', width: 5.4, height: 3.0});
        tooltip.setAttribute('material', {color: '#333', opacity: 1});
        tooltip.setAttribute('rounded', 'radius: 0.05');
        tooltip.setAttribute('visible', 'false');

        let tooltipText = document.createElement('a-text');
        tooltipText.setAttribute('value', '');
        tooltipText.setAttribute('align', 'center');
        tooltipText.setAttribute('width', '5.1');
        tooltipText.setAttribute('color', 'white');
        tooltipText.setAttribute('position', '0 0 0.01');
        tooltip.appendChild(tooltipText);

        this.el.appendChild(tooltip);
        return { plane: tooltip, text: tooltipText };
    },

    createTimelineLine: function() {
        const timelineLine = document.createElement('a-box');
        timelineLine.setAttribute('width', '0.01');
        timelineLine.setAttribute('height', '3.9');
        timelineLine.setAttribute('depth', '0.01');
        timelineLine.setAttribute('color', 'yellow');
        timelineLine.setAttribute('opacity', '0.8');
        timelineLine.setAttribute('position', '0 0 -0.39'); // Much closer to candlesticks at -0.4
        timelineLine.setAttribute('visible', false);

        // Add smooth transition animation
        timelineLine.setAttribute('animation__position', {
            property: 'position',
            dur: 400, // 400ms duration (80% of 500ms typical animation frame rate)
            easing: 'easeInOutQuad'
        });

        this.el.appendChild(timelineLine);
        return timelineLine;
    },

    animateTimelineLine: function (targetX) {
        if (!this.timelineLine) return;

        // Make the line visible
        this.timelineLine.setAttribute('visible', 'true');

        // Get current position
        const currentPosition = this.timelineLine.getAttribute('position');
        const startX = currentPosition ? currentPosition.x : 0;

        // Animate the line to the target position
        this.timelineLine.setAttribute('animation', {
            property: 'position',
            from: `${startX} 0 0`,
            to: `${targetX} 0 0`,
            dur: 200,
            easing: 'linear'
        });

        // console.log(`Animated timeline line from x=${startX} to x=${targetX}`);
    },

    updateLegend: function () {
        if (!this.stockPoints || !this.stockPoints.length || !this.legendText) return;

        const visibleData = this.stockPoints.slice(this.visibleStartIndex, this.visibleStartIndex + this.data.intervals);
        const startDate = new Date(visibleData[0].DateTime).toISOString().split('T')[0];
        const endDate = new Date(visibleData[visibleData.length - 1].DateTime).toISOString().split('T')[0];
        const totalVolume = visibleData.reduce((sum, d) => sum + (d.Volume || 0), 0).toLocaleString();

        const stockName = (this.stockMeta.tickerDetails?.name || 'S&P 500 ETF').slice(0, 36);
        const exchange = this.stockMeta.tickerDetails?.primary_exchange || 'NASDAQ';
        // Construct candlestick interval string
        const intervalText = `${this.data.multiplier} ${this.data.timespan}${parseInt(this.data.multiplier) > 1 ? 's' : ''} candlesticks`;

        // Add timezone indicator to legend
        const timezoneText = this.useLocalTimezone ? 'Local Time' : 'Eastern Time';

        const legendText = `${stockName}\n` +
            `${this.ticker}\n` +
            `${exchange}\n` +
            `Vol: ${totalVolume}\n` +
            `${startDate} to ${endDate}\n` +
            `${intervalText}\n` +
            `${timezoneText}`;
        this.legendText.setAttribute('value', legendText);

        const maxPrice = Math.max(...visibleData.map(d => d.High));
        const minPrice = Math.min(...visibleData.map(d => d.Low));
        const priceRange = maxPrice - minPrice || 1;
        const paddingFactor = 0.9;
        const verticalScaleFactor = 0.85;
        const heightScale = (4 * paddingFactor * verticalScaleFactor) / priceRange;
        const lastCandle = visibleData[visibleData.length - 1];
        const highHeight = (lastCandle.High - minPrice) * heightScale - (2 * paddingFactor);
        const upperChartLimit = 2 * paddingFactor;

        // let xPosition = highHeight > upperChartLimit - 0.5 ? -2 : 2;
        let xPosition = 0;
        this.legendPlane.setAttribute('position', `${xPosition} ${upperChartLimit + .3} 0`);
    },

    renderGridLines: function () {
        this.clearGridLines();
        if (!this.stockPoints || !this.stockPoints.length) return;

        const visibleData = this.stockPoints.slice(this.visibleStartIndex, this.visibleStartIndex + this.data.intervals);
        const maxPrice = Math.max(...visibleData.map(d => d.High));
        const minPrice = Math.min(...visibleData.map(d => d.Low));
        const priceRange = maxPrice - minPrice || 1;
        const paddingFactor = 0.9;
        const verticalScaleFactor = 0.85;
        const heightScale = (4 * paddingFactor * verticalScaleFactor) / priceRange;

        const priceStep = priceRange / 4;
        for (let i = 0; i <= 4; i++) {
            const price = minPrice + (i * priceStep);
            const yPos = (price - minPrice) * heightScale - (2 * paddingFactor);
            const line = document.createElement('a-entity');
            line.setAttribute('line', {start: '-3.5 0 -0.35', end: '3.5 0 -0.35', color: '#666'});
            line.setAttribute('position', `0 ${yPos} 0`);
            this.el.appendChild(line);
            this.gridLines.push(line);

            const priceText = document.createElement('a-troika-text');
            priceText.setAttribute('position', `-3.85 ${yPos} -0.45`);
            priceText.setAttribute('value', "$" + price.toFixed(2));
            priceText.setAttribute('font-size', '.16');
            priceText.setAttribute('align', 'right');
            priceText.setAttribute('color', '#FFF');

            this.el.appendChild(priceText);
            this.gridLines.push(priceText);
        }

        const timeStep = Math.max(1, Math.floor(this.data.intervals / 5));
        for (let i = 0; i < visibleData.length; i += timeStep) {
            const xPos = (i / (this.data.intervals - 1)) * 5.8 - 2.9;
            const line = document.createElement('a-entity');
            line.setAttribute('line', {start: '0 -1.8 -0.35', end: '0 1.8 -0.35', color: '#666'});
            line.setAttribute('position', `${xPos} 0 0`);
            this.el.appendChild(line);
            this.gridLines.push(line);

            const date = new Date(visibleData[i].DateTime);
            const timeInfo = this.formatTimeForTimezone(date);

            const timeText = document.createElement('a-troika-text');
            timeText.setAttribute('value', `${timeInfo.date}\n${timeInfo.time.substring(0, 5)}`);
            timeText.setAttribute('font-size', '.16');
            timeText.setAttribute('align', 'center');
            timeText.setAttribute('position', `${xPos} -2.1 -0.35`);

            this.el.appendChild(timeText);
            this.gridLines.push(timeText);
        }
    },

    clearGridLines: function () {
        this.gridLines.forEach(line => this.el.removeChild(line));
        this.gridLines = [];
    },

    // Update the existing setLatestDataVisible function to include a comment for clarity
    
    setLatestDataVisible: function() {
        if (!this.stockPoints || this.stockPoints.length === 0) return;

    // For 1D charts, start from the latest data (end of array) and work backwards
        const totalDataPoints = this.stockPoints.length;
        const maxVisible = this.data.intervals;

        // Set visible start index to show most recent data
        this.visibleStartIndex = Math.max(0, totalDataPoints - maxVisible);

        // console.log(`Set latest visible range: showing points ${this.visibleStartIndex} to ${this.visibleStartIndex + Math.min(maxVisible, totalDataPoints)} of ${totalDataPoints} total`);
    },

    // Add a helper function to determine which visibility function to use
    setEarliestDataVisible: function() {
        if (!this.stockPoints || this.stockPoints.length === 0) return;

        // Show earliest data (from the beginning)
        this.visibleStartIndex = 0;

        // console.log(`Set earliest visible range: showing points ${this.visibleStartIndex} to ${this.visibleStartIndex + Math.min(this.data.intervals, this.stockPoints.length)} of ${this.stockPoints.length} total`);
    },

    renderCandlesticks: function () {
        this.clearCandlesticks();
        if (!this.stockPoints || !this.stockPoints.length) return;

        // Always show exactly 100 candlesticks per page
        const visibleCount = 100;
        const visibleData = this.stockPoints.slice(this.visibleStartIndex, this.visibleStartIndex + visibleCount);

        if (visibleData.length === 0) {
            console.warn('No visible data to render');
            return;
        }

        const maxPrice = Math.max(...visibleData.map(d => d.High));
        const minPrice = Math.min(...visibleData.map(d => d.Low));
        const priceRange = maxPrice - minPrice || 1;
        const paddingFactor = 0.9;
        const verticalScaleFactor = 0.85;
        const heightScale = (4 * paddingFactor * verticalScaleFactor) / priceRange;

        // Chart dimensions - always fill the full width with 100 candlesticks
        const chartWidth = 5.8;
        const startOffset = -2.9;

        // Calculate spacing for exactly 100 candlesticks
        const spacing = this.data.candlestickSpacing;
        const totalSpacingWidth = spacing * (visibleCount - 1);
        const availableWidthForCandlesticks = chartWidth - totalSpacingWidth;
        const candlestickWidth = Math.min(0.06, availableWidthForCandlesticks / visibleCount);

        // console.log(`📊 Rendering page ${Math.floor(this.visibleStartIndex / 100) + 1}: candlesticks ${this.visibleStartIndex}-${this.visibleStartIndex + visibleData.length - 1}`);
        // console.log(`📊 Chart layout: ${visibleData.length} candlesticks, width=${candlestickWidth.toFixed(4)}, spacing=${spacing}`);

        visibleData.forEach((d, index) => {
            const candleColor = d.Close >= d.Open ? 'green' : 'red';
            const openHeight = (d.Open - minPrice) * heightScale - (2 * paddingFactor);
            const closeHeight = (d.Close - minPrice) * heightScale - (2 * paddingFactor);
            const highHeight = (d.High - minPrice) * heightScale - (2 * paddingFactor);
            const lowHeight = (d.Low - minPrice) * heightScale - (2 * paddingFactor);

            const candleHeight = Math.abs(closeHeight - openHeight);
            const wickHeight = highHeight - lowHeight;

            // Position calculation - left-aligned with equal spacing
            const spacingOffset = index * spacing;
            const candlestickOffset = index * candlestickWidth;
            const xPosition = startOffset + spacingOffset + candlestickOffset + candlestickWidth/2;

            const candleYPosition = (closeHeight + openHeight) / 2;

            const candleBody = document.createElement('a-box');
            candleBody.setAttribute('width', candlestickWidth.toString());
            candleBody.setAttribute('height', candleHeight.toString());
            candleBody.setAttribute('depth', candlestickWidth.toString());
            candleBody.setAttribute('color', candleColor);
            candleBody.setAttribute('position', `${xPosition} ${candleYPosition} -0.4`);
            candleBody.setAttribute('data-raycastable', '');

            const wick = document.createElement('a-box');
            const wickWidth = Math.min(0.01, candlestickWidth * 0.167);
            wick.setAttribute('width', wickWidth.toString());
            wick.setAttribute('height', wickHeight.toString());
            wick.setAttribute('depth', wickWidth.toString());
            wick.setAttribute('color', 'white');
            wick.setAttribute('position', `${xPosition} ${(highHeight + lowHeight) / 2} -0.4`);

            candleBody.addEventListener('mouseenter', () => {
                this.showTooltip(d, xPosition, highHeight);
            });
            candleBody.addEventListener('mouseleave', () => {
                this.hideTooltip();
            });

            this.el.appendChild(candleBody);
            this.el.appendChild(wick);
            this.candlesticks.push(candleBody, wick);

            // Add split markers if applicable
            const splitEvent = this.splits.find(split =>
                new Date(split.execution_date).getTime() === new Date(d.DateTime).getTime()
            );
            if (splitEvent) {
                const splitMarker = document.createElement('a-box');
                splitMarker.setAttribute('width', '0.03');
                splitMarker.setAttribute('height', '0.8');
                splitMarker.setAttribute('depth', '0.05');
                splitMarker.setAttribute('color', 'yellow');
                splitMarker.setAttribute('position', `${xPosition} ${2 * paddingFactor} 0`);
                this.el.appendChild(splitMarker);
                this.candlesticks.push(splitMarker);
            }
        });

        // console.log(`✅ Rendered ${visibleData.length} candlesticks on page ${Math.floor(this.visibleStartIndex / 100) + 1} of ${Math.ceil(this.stockPoints.length / 100)}`);
    },

    showTooltip: function (data, xPos, highHeight) {
        const date = new Date(data.DateTime);
        const timeInfo = this.formatTimeForTimezone(date);

        const stockName = this.stockMeta.tickerDetails?.name || 'S&P 500 ETF';
        const exchange = this.stockMeta.tickerDetails?.primary_exchange || 'N/A';
        const tooltipText =
            `Time: ${timeInfo.date} ${timeInfo.time} ${timeInfo.timezone}\n` +
            `High: ${data.High.toFixed(2)}\n` +
            `Low: ${data.Low.toFixed(2)}\n` +
            `Open: ${data.Open.toFixed(2)}\n` +
            `Close: ${data.Close.toFixed(2)}`;
        this.tooltip.text.setAttribute('value', tooltipText);

        const lines = tooltipText.split('\n').length;
        const textWidth = 2;
        const textHeight = lines * 0.3;
        this.tooltip.plane.setAttribute('geometry', {
            primitive: 'plane',
            width: textWidth,
            height: textHeight
        });

        this.tooltip.plane.setAttribute('position', `${xPos} ${highHeight + (textHeight / 2) + 0.3} 0.1`);
        this.tooltip.plane.setAttribute('visible', 'true');
    },

    hideTooltip: function () {
        this.tooltip.plane.setAttribute('visible', 'false');
    },

    clearCandlesticks: function () {
        this.candlesticks.forEach(candle => this.el.removeChild(candle));
        this.candlesticks = [];
    }
});